import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCampaign,
  useCampaigns,
  useClaimCampaign,
  useDeleteCampaign,
  useUpdateCampaign,
} from '../lib/api/campaigns';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { Twemoji } from '../components/ui/Twemoji';
import { Chip } from '../components/ui/Chip';
import { BrandAvatar, OwnerAvatar } from '../lib/brandAvatars';
import { fmtShort, parseDate, dayDiff, today, daysFromToday } from '../lib/dates';
import { useToast } from '../components/ui/Toast';
import CreateCampaignModal from '../components/modals/CreateCampaignModal';
import DuplicateCampaignModal from '../components/modals/DuplicateCampaignModal';
import AttachmentsRow from '../components/attachments/AttachmentsRow';
import NotesCard from '../components/notes/NotesCard';
import { useAttachments } from '../lib/api/attachments';
import AssetsSection from '../components/assets/AssetsSection';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PricyModal from '../components/modals/PricyModal';
import ShareCampaignModal from '../components/modals/ShareCampaignModal';
import PartnershipStrip from '../components/partnerships/PartnershipStrip';
import { splitCampaignName } from '../lib/campaignName';

const typeColor = (t: string) =>
  t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)';

const typeColorBg = (t: string) =>
  t === 'Promotion' ? 'var(--sitewide-bg)' : t === 'Brand' ? 'var(--brand-bg)' : 'var(--seasonal-bg)';

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="section">
    <div className="section-head">
      <div className="section-title">{children}</div>
    </div>
  </div>
);

const CampaignDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: campaign, isLoading, isError } = useCampaign(id);
  const { data: allCampaigns = [] } = useCampaigns();
  const claimMutation = useClaimCampaign();
  const deleteMutation = useDeleteCampaign();
  const updateMutation = useUpdateCampaign();
  const [menuOpen, setMenuOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [pricyOpen, setPricyOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const { data: attachments = [] } = useAttachments({ campaign: id ?? '' });

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
        Loading…
      </div>
    );
  }
  if (isError || !campaign) {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-eyebrow">Campaign</div>
            <h1 className="page-title">Not found</h1>
          </div>
          <Button variant="ghost" onClick={() => navigate('/campaigns')}>
            <Icon name="chevronLeft" size={13} /> Back
          </Button>
        </div>
      </div>
    );
  }

  const children = allCampaigns.filter((c) => c.parent === campaign.id);
  const parent = campaign.parent
    ? allCampaigns.find((c) => c.id === campaign.parent)
    : null;

  // Timing — only meaningful when the campaign actually has dates.
  const isScheduled = !!(campaign.start_date && campaign.end_date);
  const startDate = isScheduled ? parseDate(campaign.start_date as string) : null;
  const elapsedDays = startDate ? dayDiff(startDate, today()) : 0;
  const daysLeft = isScheduled ? daysFromToday(campaign.end_date as string) : 0;
  let timingLabel: string;
  if (!isScheduled) timingLabel = '· dates to plan';
  else if (elapsedDays < 0) timingLabel = `· starts in ${-elapsedDays}d`;
  else if (daysLeft < 0) timingLabel = `· ended ${-daysLeft}d ago`;
  else if (daysLeft === 0) timingLabel = '· ends today';
  else timingLabel = `· ends in ${daysLeft}d`;
  const accent = typeColor(campaign.type);
  const accentBg = typeColorBg(campaign.type);
  const inProgress = isScheduled && elapsedDays >= 0 && daysLeft >= 0;

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.push('Link copied');
  };

  const claim = async () => {
    try {
      await claimMutation.mutateAsync(campaign.id);
      toast.push('You are now the owner');
      setMenuOpen(false);
    } catch {
      toast.push('Could not claim campaign');
    }
  };

  const del = async () => {
    try {
      await deleteMutation.mutateAsync(campaign.id);
      toast.push('Campaign deleted');
      navigate('/campaigns');
    } catch {
      toast.push('Could not delete campaign');
    }
    setConfirmDelete(false);
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="link"
          onClick={() => navigate('/campaigns')}
          style={{ fontSize: 12 }}
        >
          ← All campaigns
        </button>
      </div>

      {/* Business-card hero — 2 columns: main info left, attachments/notes right */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ height: 4, background: accent }} />
        <div style={{ padding: '18px 22px' }}>
          {/* Top action strip — owner + actions, all on the right */}
          <div
            className="m-wrap"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 10,
              position: 'relative',
              marginBottom: 8,
            }}
          >
            {campaign.owner_details && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  marginRight: 4,
                }}
              >
                <OwnerAvatar user={campaign.owner_details} size={22} />
                {campaign.owner_details.display_name ?? campaign.owner_details.username}
              </div>
            )}
            <Button variant="ghost" onClick={copyLink}>
              <Icon name="link" size={13} /> Copy link
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShareOpen(true)}
              title="Share a read-only schedule page with each brand partner — one link per brand"
            >
              <Icon name="globe" size={13} /> Share with partner
              {campaign.shares?.some((l) => l.enabled) && (
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--seasonal)', marginLeft: 2 }} />
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(`/campaigns/${campaign.id}/lookbook`)}
              title="Open the campaign lookbook (export to PDF from there)"
            >
              <Icon name="image" size={13} /> Lookbook
            </Button>
            {campaign.pricy_campaign_id ? (
              <>
              <Button
                variant="ghost"
                onClick={() => navigate(`/campaigns/${campaign.id}/discounts`)}
                title="Every brand in the Pricy campaign with its discount, plus an Excel download"
              >
                <Icon name="tag" size={13} /> Brands &amp; Discounts
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  if (campaign.pricy_campaign_url) window.open(campaign.pricy_campaign_url, '_blank');
                }}
                title={`Linked to Pricy campaign #${campaign.pricy_campaign_id}`}
                style={{ color: 'var(--seasonal)' }}
              >
                <Icon name="tag" size={13} /> Pricy #{campaign.pricy_campaign_id} ↗
              </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                onClick={() => setPricyOpen(true)}
                title="Create the pricing campaign (discount) in Pricy from this campaign's data"
              >
                <Icon name="tag" size={13} /> Set up in Pricy
              </Button>
            )}
            <div style={{ position: 'relative' }}>
              <Button variant="ghost" onClick={() => setMenuOpen((v) => !v)}>
                <Icon name="more" size={13} />
              </Button>
              {menuOpen && (
                <div className="popover" style={{ right: 0, top: 34 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="popover-item"
                    onClick={() => {
                      setEditOpen(true);
                      setMenuOpen(false);
                    }}
                  >
                    <Icon name="settings" size={13} /> Edit campaign
                  </button>
                  <button type="button" className="popover-item" onClick={claim}>
                    <Icon name="user" size={13} /> Claim ownership
                  </button>
                  <button
                    type="button"
                    className="popover-item"
                    onClick={() => {
                      setDuplicateOpen(true);
                      setMenuOpen(false);
                    }}
                  >
                    <Icon name="link" size={13} /> Duplicate campaign
                  </button>
                  <button
                    type="button"
                    className="popover-item"
                    style={{ color: 'var(--warn)' }}
                    onClick={() => {
                      setConfirmDelete(true);
                      setMenuOpen(false);
                    }}
                  >
                    <Icon name="x" size={13} /> Delete campaign
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 2-column body */}
          <div className="m-stack" style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 32, alignItems: 'stretch' }}>
            {/* LEFT: identity + timing + brands */}
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip kind="type" value={campaign.type} />
                {parent && (
                  <span className="chip neutral">Nested under {parent.name}</span>
                )}
                {children.length > 0 && (
                  <span className="chip neutral">{children.length} nested</span>
                )}
              </div>

              {(() => {
                const { eyebrow, title } = splitCampaignName(campaign.name);
                return (
                  <h1 className="page-title" style={{ fontSize: 28, marginTop: 18, marginBottom: 14 }}>
                    {campaign.emoji && <span style={{ marginRight: 10 }}><Twemoji emoji={campaign.emoji} size={26} /></span>}
                    {eyebrow && (
                      <span style={{ color: 'var(--ink-4)', fontWeight: 450, marginRight: 10 }}>
                        {eyebrow}
                      </span>
                    )}
                    {title}
                  </h1>
                );
              })()}

              {campaign.description && (
                <p style={{ color: 'var(--ink-3)', margin: '0 0 14px', fontSize: 13 }}>
                  {campaign.description}
                </p>
              )}

              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 18,
                }}
              >
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: accentBg,
                    color: accent,
                    flexShrink: 0,
                  }}
                >
                  <Icon name="calendar" size={15} />
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'flex-end',
                    gap: 8,
                  }}
                >
                  <span
                    className="mono"
                    style={{
                      fontSize: 17,
                      fontWeight: 600,
                      color: 'var(--ink)',
                      letterSpacing: '-0.01em',
                      lineHeight: 1,
                    }}
                  >
                    {isScheduled ? (
                      <>
                        {fmtShort(campaign.start_date as string)}{' '}
                        <span style={{ color: 'var(--ink-4)' }}>→</span>{' '}
                        {fmtShort(campaign.end_date as string)}
                      </>
                    ) : (
                      <span style={{ color: 'var(--ink-4)' }}>Dates to be confirmed</span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: accent,
                      letterSpacing: 0,
                      lineHeight: 1.3,
                    }}
                  >
                    {timingLabel}
                  </span>
                </span>
              </div>

              {/* Brand pills — no surrounding bg; just a quiet eyebrow + the pills */}
              {campaign.brand_details && campaign.brand_details.length > 0 && (
                <div>
                  <div
                    style={{
                      fontSize: 10.5,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: 'var(--ink-4)',
                      fontWeight: 600,
                      marginBottom: 10,
                    }}
                  >
                    {campaign.brand_details.length === 1
                      ? 'Brand'
                      : `${campaign.brand_details.length} brands`}
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {campaign.brand_details.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => navigate(`/brands/${b.id}`)}
                        title={`Open ${b.name}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '4px 12px 4px 4px',
                          background: 'var(--bg-elevated)',
                          border: '1px solid var(--border)',
                          borderRadius: 999,
                          cursor: 'pointer',
                          fontSize: 13,
                          color: 'var(--ink-2)',
                          fontWeight: 500,
                          transition: 'all 0.12s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--bg-hover)';
                          e.currentTarget.style.borderColor = 'var(--border-strong)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--bg-elevated)';
                          e.currentTarget.style.borderColor = 'var(--border)';
                        }}
                      >
                        <BrandAvatar brand={b} size={22} />
                        {b.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: collapsible resource chips (attachments + notes) —
                anchored to the bottom of the hero so they read as a
                quiet tools row, not floating top-right pills. */}
            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              {(() => {
                const hasNotes =
                  !!campaign.notes && campaign.notes.trim().length > 0;
                const fileCount = attachments.length;
                const chipBase: React.CSSProperties = {
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px',
                  borderRadius: 999,
                  fontSize: 11.5,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                  border: '1px solid var(--border)',
                  background: 'transparent',
                };
                const filled: React.CSSProperties = { color: 'var(--ink-3)' };
                const empty: React.CSSProperties = { color: 'var(--ink-4)' };
                return (
                  <>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => setAttachmentsOpen((v) => !v)}
                        style={{
                          ...chipBase,
                          ...(fileCount > 0 ? filled : empty),
                          background: attachmentsOpen
                            ? 'var(--bg-subtle)'
                            : 'transparent',
                        }}
                      >
                        <Icon name="link" size={11} />
                        {fileCount > 0
                          ? `${fileCount} file${fileCount === 1 ? '' : 's'}`
                          : 'Add attachment'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setNotesOpen((v) => !v)}
                        style={{
                          ...chipBase,
                          ...(hasNotes ? filled : empty),
                          background: notesOpen
                            ? 'var(--bg-subtle)'
                            : 'transparent',
                        }}
                      >
                        <Icon name="settings" size={11} />
                        Notes
                        {hasNotes && (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 50,
                              background: 'var(--accent-1, #3B5CFF)',
                              display: 'inline-block',
                            }}
                          />
                        )}
                      </button>
                    </div>
                    {attachmentsOpen && (
                      <div style={{ marginTop: 10 }}>
                        <AttachmentsRow campaignId={campaign.id} />
                      </div>
                    )}
                    {notesOpen && (
                      <div style={{ marginTop: 10 }}>
                        <NotesCard
                          value={campaign.notes ?? ''}
                          onSave={async (notes) => {
                            await updateMutation.mutateAsync({
                              id: campaign.id,
                              patch: { notes },
                            });
                          }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* NESTED CAMPAIGNS */}
      {children.length > 0 && (
        <>
          <SectionLabel>Nested campaigns</SectionLabel>
          <div
            className="grid"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
          >
            {children.map((ch) => (
              <button
                key={ch.id}
                type="button"
                className="card"
                onClick={() => navigate(`/campaigns/${ch.id}`)}
                style={{ textAlign: 'left', padding: 0, cursor: 'pointer' }}
              >
                <div style={{ height: 4, background: typeColor(ch.type) }} />
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Chip kind="type" value={ch.type} />
                  </div>
                  <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>{ch.name}</div>
                  <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-4)' }}>
                    {ch.start_date && ch.end_date
                      ? `${fmtShort(ch.start_date)} → ${fmtShort(ch.end_date)}`
                      : 'To plan'}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      <div className="section">
        <PartnershipStrip campaignId={campaign.id} />
      </div>

      <AssetsSection campaign={campaign} />

      <CreateCampaignModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        campaign={campaign}
      />
      <ShareCampaignModal open={shareOpen} onClose={() => setShareOpen(false)} campaign={campaign} />
      <PricyModal
        open={pricyOpen}
        onClose={() => setPricyOpen(false)}
        campaign={campaign}
      />
      <DuplicateCampaignModal
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        source={campaign}
        onCreated={(newId) => navigate(`/campaigns/${newId}`)}
      />
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={del}
        title={`Delete "${campaign.name}"?`}
        description="This will permanently remove the campaign and all its tasks and social posts. This cannot be undone."
        confirmLabel="Delete campaign"
        danger
        busy={deleteMutation.isPending}
      />
    </div>
  );
};

export default CampaignDetail;
